import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, forkJoin, of } from 'rxjs';
import { ParkingLotDetail } from '@core/models/parking-detail.model';
import { ParkingService } from '../services/parking.service';
import { ParkingLotSlotsResponse } from '@core/models/parking-slot.model';
import { MenuService } from '@core/services/menu.service';
import { ROUTES, SPOT_TYPE_LABEL, USER_ROLES } from '@shared/constant/shared.constant';
import { Booking, BookingType } from '@core/models/booking.model';
import { BreadcrumbItem, CompanyService } from '@shared/services/company.service';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { SuperAdminService } from '@core/services/super-admin.service';

interface SpotStatusRow {
  spotName: string;
  spotType: string;
  statusText: string;
  statusLink: string[] | null;
  statusQueryParams: Record<string, unknown> | null;
  arrivalDate: Date | null;
  departureDate: Date | null;
  showDepartureDate: boolean;
  bookingTypeText: string;
  bookingRows: SpotBookingRow[];
  slotId: string | null;
}

interface BookingSpotInfo {
  booking: Booking;
  arrival: Date | null;
  departure: Date | null;
}

interface BookingMatchIndex {
  bySlotId: Map<string, BookingSpotInfo[]>;
  bySlotNameAndType: Map<string, BookingSpotInfo[]>;
}

interface SpotBookingRow {
  email: string | null;
  statusLink: string[] | null;
  statusQueryParams: Record<string, unknown> | null;
  bookingTypeText: string;
  arrivalDate: Date | null;
  departureDate: Date | null;
  showDepartureDate: boolean;
}

interface BookingListApiResponse {
  data?: {
    bookingList?: Booking[];
  };
}

@Component({
  selector: 'app-view-parking',
  templateUrl: './view-parking.component.html',
  styleUrls: ['./view-parking.component.scss']
})
export class ViewParkingComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  spotTypeMsg: string = SPOT_TYPE_LABEL;
  id = "";
  parkingLotData: ParkingLotDetail;
  parkingLotSlotsOutput: any = [];
  slotsUnderMaintenance: any = [];
  editParkingCheck: boolean = false;
  groupedRates = [];
  role : string;
  companyId : string;
  userRoles = USER_ROLES;
  fullLengthAvailable = 0;
  fullLengthTotal = 0;
  halfLengthAvailable = 0;
  halfLengthTotal = 0;
  startDate: Date | null = null;
  endDate: Date | null = null;
  slotStatusRows: SpotStatusRow[] = [];
  bookingList: Booking[] = [];
  sortKey: 'spotName' | 'spotType' | 'statusText' | 'bookingTypeText' | 'arrivalDate' | 'departureDate' = 'spotName';
  sortDirection: 'asc' | 'desc' = 'asc';
  expandedSpotKeys = new Set<string>();

  constructor(
    private readonly parkingService: ParkingService,
    private readonly route: ActivatedRoute,
    private readonly menuService: MenuService,
    private readonly router: Router,
    private readonly companyService: CompanyService,
    private readonly superAdminService: SuperAdminService,
    private readonly encryptStorage: EncryptionStorageService) { }

  ngOnInit() {
    this.setDefaultDateRange();
    this.role = this.encryptStorage.getItem('roleName'); 
    this.companyId = this.encryptStorage.getItem('companyId');
    this.editParkingCheck = this.menuService.shortNameExists(JSON.parse(this.encryptStorage.getItem('menuData')), 71)
    
    this.getParkingLotId();

    const breadcrumbObj: BreadcrumbItem[] = [
      {
        title: 'Parking Locations',
        url: `super-admin/parking-list/${this.companyId}`
      },
      {
        title: 'View Parking'
      }
    ];
    this.companyService.setAdminHeaders('0', breadcrumbObj);
  }

  private setDefaultDateRange(): void {
    const today = this.normalizeDate(new Date());
    this.startDate = today;
    this.endDate = today;
  }

  private setDefaultDateRangeForParkAnywhere(): void {
    const today = this.normalizeDate(new Date());
    const startOfMonth = this.normalizeDate(
      new Date(today.getFullYear(), today.getMonth(), 1)
    );
    const endOfMonth = this.normalizeDate(
      new Date(today.getFullYear(), today.getMonth() + 1, 0)
    );
    this.startDate = startOfMonth;
    this.endDate = endOfMonth;
  }

getParkingLotId(): void {
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      this.getAllParkingLotData();
    });
  }
  getAllParkingLotData(): void {
    
    combineLatest([
      this.parkingService.getParkingLotsById(this.id)
        .pipe(
          catchError(() => {
            return of(null)
          }),
        ),
      this.parkingService.getParkingLotDetailsWithSlots(this.id)
        .pipe(
          catchError(() => {
            return of(null)
          }),
        )
    ])
      .pipe(autoUnsubWith(this.destroyRef)).subscribe({
        next: ([_parkingLot, _parkingLotDetail]: [ParkingLotDetail, ParkingLotSlotsResponse]) => {
          if (_parkingLot) {
            this.parkingLotData = _parkingLot;
            this.groupRatesByType();
            if (this.parkingLotData.isParkAnywhere) {
              this.setDefaultDateRangeForParkAnywhere();
            }
          }
          if (_parkingLotDetail) {
            this.parkingLotSlotsOutput = _parkingLotDetail.parkingLotSlotsOutput;
            this.slotsUnderMaintenance = _parkingLotDetail.slotsUnderMaintenance || [];
          }
          this.fetchBookingData();
        }
      })
  }

  getPlainText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
  
editParkingNavigation(): void {
    let routePath = '';

    switch (this.role) {
      case 'SuperAdmin':
        routePath = 'super-admin/parking/edit';
        break;
      case 'ParkingManager':
        routePath = 'manager/parking/edit';
        break;
      case 'ParkingOwner':
        routePath = 'manager/parking/edit';
        break;
      default:
        return; // Prevent navigation for unauthorized users
    }

    this.router.navigate([routePath, this.id]);
  }
  
  hasRates(specialRates) {
    return specialRates.rates && specialRates.rates.length > 0;
  }

   groupRatesByType() {
    this.groupedRates = this.parkingLotData?.spotTypeSpecialRate.reduce((acc, current) => {
      const existingGroup = acc.find(group => group.spotTypeName === current.spotTypeName);
      if (existingGroup) {
        existingGroup.rates.push(current);
      } else {
        acc.push({
          spotTypeName: current.spotTypeName,
          publicFareChargesDaily: current.publicFareChargesDaily,
          publicFareChargesMonthly: current.publicFareChargesMonthly,
          description: current.description,
          rates: [current]
        });
      }
      return acc;
    }, []);
  }

  private updateAvailabilitySummary(): void {
    const totals = this.parkingLotData?.isParkAnywhere
      ? this.getAvailabilityTotalsForParkAnywhere()
      : this.getAvailabilityTotalsFromRows();
    this.fullLengthAvailable = totals.fullLengthAvailable;
    this.halfLengthAvailable = totals.halfLengthAvailable;
    this.fullLengthTotal = totals.fullLengthTotal;
    this.halfLengthTotal = totals.halfLengthTotal;
  }

  onStartDateChange(date: Date): void {
    const normalized = this.normalizeDate(date);
    this.startDate = normalized;
    if (this.endDate && this.endDate < normalized) {
      this.endDate = normalized;
    }
    this.fetchBookingData();
  }

  onEndDateChange(date: Date): void {
    const normalized = this.normalizeDate(date);
    this.endDate = normalized;
    this.fetchBookingData();
  }

  private normalizeDate(value: Date): Date {
    const normalized = new Date(value);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  setSort(
    key: 'spotName' | 'spotType' | 'statusText' | 'bookingTypeText' | 'arrivalDate' | 'departureDate'
  ): void {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }
    this.sortKey = key;
    this.sortDirection = 'asc';
  }

  get sortedSlotStatusRows(): SpotStatusRow[] {
    const rows = [...this.slotStatusRows];
    rows.sort((a, b) => {
      let result = 0;
      if (this.sortKey === 'spotName') {
        result = this.compareSpotNames(a.spotName, b.spotName);
      } else if (this.sortKey === 'spotType') {
        result = a.spotType.localeCompare(b.spotType);
      } else if (this.sortKey === 'statusText') {
        result = a.statusText.localeCompare(b.statusText);
      } else if (this.sortKey === 'bookingTypeText') {
        result = a.bookingTypeText.localeCompare(b.bookingTypeText);
      } else if (this.sortKey === 'arrivalDate') {
        result = this.compareDates(a.arrivalDate, b.arrivalDate);
      } else {
        result = this.compareDates(a.departureDate, b.departureDate);
      }
      return this.sortDirection === 'asc' ? result : -result;
    });
    return rows;
  }
  private updateSlotStatusRows(): void {
    this.slotStatusRows = this.buildSlotStatusRows();
    this.updateAvailabilitySummary();
  }

  private buildSlotStatusRows(): SpotStatusRow[] {
    const rows: SpotStatusRow[] = [];
    const bookingIndex = this.buildBookingMatchIndex();

    (this.parkingLotSlotsOutput || []).forEach((vehicle) => {
      const slotType = this.getSpotTypeLabel(vehicle?.vehicleName);
      (vehicle?.slots || []).forEach((slot) => {
        const slotName = slot?.slotName || '';
        const bookingInfos = this.findBookingsForSlot(
          bookingIndex,
          slot?.id,
          slotName,
          vehicle?.vehicleTypeId
        );
        const bookingRows = this.buildSpotBookingRows(bookingInfos);
        const primaryBooking = bookingRows[0];
        const email = primaryBooking?.email || null;
        const profileLink = primaryBooking?.statusLink
          ? { link: primaryBooking.statusLink, queryParams: primaryBooking.statusQueryParams }
          : null;
        rows.push({
          spotName: slotName,
          spotType: slotType,
          statusText: bookingRows.length
            ? this.buildStatusText(email)
            : 'Open',
          statusLink: email && profileLink ? profileLink.link : null,
          statusQueryParams: email && profileLink ? profileLink.queryParams : null,
          arrivalDate: primaryBooking?.arrivalDate ?? null,
          departureDate: primaryBooking?.departureDate ?? null,
          showDepartureDate: primaryBooking?.showDepartureDate ?? false,
          bookingTypeText: primaryBooking?.bookingTypeText ?? '-',
          bookingRows,
          slotId: slot?.id || null,
        });
      });
    });

    return rows.sort((a, b) => this.compareSpotNames(a.spotName, b.spotName));
  }

  private getSpotTypeLabel(vehicleName: string): string {
    const lowerName = (vehicleName || '').toLowerCase();
    return lowerName.includes('full-length') ? 'Full Length' : 'Half Length';
  }

  private compareSpotNames(a: string, b: string): number {
    const [aPrefix, aNum] = this.splitSpotName(a);
    const [bPrefix, bNum] = this.splitSpotName(b);
    if (aPrefix !== bPrefix) {
      return aPrefix.localeCompare(bPrefix);
    }
    return aNum - bNum;
  }

  private compareDates(a: Date | null, b: Date | null): number {
    const aTime = a ? a.getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b ? b.getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  }

  private splitSpotName(name: string): [string, number] {
    const match = (name || '').match(/^([A-Za-z]+)(\d+)/);
    if (!match) {
      return [name || '', Number.MAX_SAFE_INTEGER];
    }
    return [match[1], parseInt(match[2], 10)];
  }

  private fetchBookingData(): void {
    if (!this.id || !this.startDate || !this.endDate) {
      this.bookingList = [];
      this.updateSlotStatusRows();
      return;
    }

    const requestBase: any = {
      startDate: this.formatDateToString(this.startDate),
      endDate: this.formatDateToString(this.endDate),
      search: '',
      pageSize: 0,
      pageNumber: 0,
      timeZone: Utility.getLocalTimeZone(),
      parkingLotIds: [this.id],
      paymentStatus: 0,
      companyId:
        this.role === this.userRoles.SUPER_ADMIN ? this.companyId : null,
      driverGroupId: null,
      userType: null,
    };

    const bookingTypes = [0, 2];
    const statuses = [BookingType.Active, BookingType.Pending, BookingType.Past];
    const requests = bookingTypes.flatMap((bookingType) =>
      statuses.map((status) => ({ ...requestBase, bookingType, status }))
    );

    const calls = requests.map((payload) =>
      this.superAdminService
        .getAllBookingsList(payload)
        .pipe(catchError(() => of(null as BookingListApiResponse | null)))
    );

    forkJoin(calls)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (responses: Array<BookingListApiResponse | null>) => {
          this.bookingList = this.mergeBookingLists(responses);
          this.updateSlotStatusRows();
        },
        error: () => {
          this.bookingList = [];
          this.updateSlotStatusRows();
        },
      });
  }

  private formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildBookingMatchIndex(): BookingMatchIndex {
    const bySlotId = new Map<string, BookingSpotInfo[]>();
    const bySlotNameAndType = new Map<string, BookingSpotInfo[]>();

    (this.bookingList || []).forEach((booking) => {
      const arrival = this.parseBookingDate(
        booking.startDate ?? booking.arrivalDate
      );
      const departure = this.parseBookingDate(
        booking.endDate ?? booking.departureDate
      );
      const bookingInfo: BookingSpotInfo = { booking, arrival, departure };
      const slotId = this.getBookingSlotId(booking);
      const spotNames = this.getBookingSpotNames(booking);
      const vehicleTypeId = this.getBookingVehicleTypeId(booking);

      if (slotId) {
        const slotKey = this.normalizeSpotKey(slotId);
        const existing = bySlotId.get(slotKey) || [];
        bySlotId.set(slotKey, [...existing, bookingInfo]);
      }

      spotNames.forEach((spotName) => {
        const key = this.buildSlotNameTypeKey(spotName, vehicleTypeId);
        const existing = bySlotNameAndType.get(key) || [];
        bySlotNameAndType.set(key, [...existing, bookingInfo]);
      });
    });

    return { bySlotId, bySlotNameAndType };
  }

  private getBookingSpotNames(booking: Booking): string[] {
    const value =
      (booking as any)?.assignedSpots ??
      (booking as any)?.slotNames ??
      booking?.slotName ??
      (booking as any)?.parkingSlot ??
      (booking as any)?.parkingSlots;
    return this.extractSpotNames(value);
  }

  private extractSpotNames(value: unknown): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === 'string' ? item : item?.slotName))
        .filter((spot) => !!spot);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((spot) => spot.trim())
        .filter((spot) => !!spot);
    }

    return [];
  }

  private parseBookingDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const parsed = new Date(value as any);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private isEarlierArrival(candidate: Date | null, existing: Date | null): boolean {
    if (!candidate) {
      return false;
    }
    if (!existing) {
      return true;
    }
    return candidate.getTime() < existing.getTime();
  }

  private normalizeSpotKey(spotName: string): string {
    return (spotName || '').trim().toLowerCase();
  }

  private buildSlotNameTypeKey(spotName: string, vehicleTypeId: string | null): string {
    const typeKey = (vehicleTypeId || '').trim().toLowerCase();
    return `${this.normalizeSpotKey(spotName)}|${typeKey}`;
  }

  private getBookingSlotId(booking: Booking): string | null {
    return (booking as any)?.slotId || null;
  }

  private getBookingVehicleTypeId(booking: Booking): string | null {
    return (booking as any)?.vehicleTypeId || null;
  }

  private findBookingsForSlot(
    bookingIndex: BookingMatchIndex,
    slotId: string | null | undefined,
    slotName: string,
    vehicleTypeId: string | null | undefined
  ): BookingSpotInfo[] {
    if (slotId) {
      const byId = bookingIndex.bySlotId.get(this.normalizeSpotKey(slotId)) || [];
      const filteredById = byId.filter((info) =>
        this.isVehicleTypeMatch(info.booking, vehicleTypeId)
      );
      if (filteredById.length) {
        return this.sortBookingInfos(filteredById);
      }
    }

    const nameKey = this.buildSlotNameTypeKey(slotName, vehicleTypeId || null);
    const byName = bookingIndex.bySlotNameAndType.get(nameKey) || [];
    const filteredByName = byName.filter((info) =>
      this.isVehicleTypeMatch(info.booking, vehicleTypeId)
    );

    return this.sortBookingInfos(filteredByName);
  }

  private sortBookingInfos(bookings: BookingSpotInfo[]): BookingSpotInfo[] {
    return [...bookings].sort((a, b) => this.compareDates(a.arrival, b.arrival));
  }

  private isVehicleTypeMatch(
    booking: Booking,
    vehicleTypeId: string | null | undefined
  ): boolean {
    const bookingTypeId = this.getBookingVehicleTypeId(booking);
    if (!bookingTypeId || !vehicleTypeId) {
      return true;
    }
    return bookingTypeId.toLowerCase() === vehicleTypeId.toLowerCase();
  }

  private buildSpotBookingRows(bookings: BookingSpotInfo[]): SpotBookingRow[] {
    return bookings.map((info) => {
      const email = this.getBookingEmail(info.booking);
      const profileLink = this.getProfileLink(info.booking);
      return {
        email,
        statusLink: profileLink?.link || null,
        statusQueryParams: profileLink?.queryParams || null,
        bookingTypeText: this.getBookingTypeText(info.booking),
        arrivalDate: info.arrival,
        departureDate: info.departure,
        showDepartureDate: this.shouldShowDepartureDate(info.booking),
      };
    });
  }

  private buildStatusText(email: string | null): string {
    if (!email) {
      return 'Booked';
    }
    return email;
  }

  private getBookingEmail(booking: Booking): string | null {
    return booking?.driverEmailId || (booking as any)?.email || null;
  }

  private getProfileLink(
    booking: Booking
  ): { link: string[]; queryParams: Record<string, unknown> } | null {
    if (!this.isAdminOrOwnerRole()) {
      return null;
    }

    const basePath = this.getRoleBasePath();
    if (!basePath) {
      return null;
    }

    const userType = ((booking as any)?.userType || '').toString().toLowerCase();
    const isGroupUser = userType
      ? userType.includes('group')
      : !!(booking as any)?.groupUserId;
    const userId = isGroupUser
      ? (booking as any)?.groupUserId || booking?.createdBy
      : booking?.createdBy;

    if (!userId) {
      return null;
    }

    if (isGroupUser) {
      return {
        link: ['/', basePath, 'group-operator-info'],
        queryParams: { Id: userId, tab: 0, iTab: 0 },
      };
    }

    return {
      link: ['/', basePath, 'driver-info'],
      queryParams: { id: userId, tab: 0, iTab: 0 },
    };
  }

  private isAdminOrOwnerRole(): boolean {
    return (
      this.role === this.userRoles.SUPER_ADMIN ||
      this.role === this.userRoles.PARKING_MANAGER ||
      this.role === this.userRoles.PARKING_OWNER
    );
  }

  private getRoleBasePath(): string | null {
    if (this.role === this.userRoles.SUPER_ADMIN) {
      return ROUTES.SUPER_ADMIN;
    }
    if (
      this.role === this.userRoles.PARKING_MANAGER ||
      this.role === this.userRoles.PARKING_OWNER
    ) {
      return ROUTES.MANAGER;
    }
    return null;
  }

  private getBookingTypeText(booking: Booking): string {
    if (booking?.bookingType === 2) {
      return 'Recurring';
    }
    if (booking?.bookingType === 0) {
      return 'One-time';
    }
    return booking?.bookingType != null ? String(booking.bookingType) : '-';
  }

  private shouldShowDepartureDate(booking: Booking): boolean {
    if (booking?.bookingType !== 2) {
      return true;
    }

    const isCancelled =
      booking?.status?.toLowerCase() === 'cancelled' ||
      (booking as any)?.isSubscriptionCancel === true;

    return isCancelled;
  }

  toggleSpotRow(row: SpotStatusRow): void {
    const key = this.getSpotRowKey(row);
    if (!key) {
      return;
    }
    if (this.expandedSpotKeys.has(key)) {
      this.expandedSpotKeys.delete(key);
      return;
    }
    this.expandedSpotKeys.add(key);
  }

  isExpanded(row: SpotStatusRow): boolean {
    const key = this.getSpotRowKey(row);
    return key ? this.expandedSpotKeys.has(key) : false;
  }

  getSpotRowKey(row: SpotStatusRow): string | null {
    if (row.slotId) {
      return this.normalizeSpotKey(row.slotId);
    }
    if (!row.spotName) {
      return null;
    }
    return `${this.normalizeSpotKey(row.spotName)}|${this.normalizeSpotKey(row.spotType)}`;
  }

  private mergeBookingLists(
    responses: Array<BookingListApiResponse | null>
  ): Booking[] {
    const bookingMap = new Map<string, Booking>();
    responses.forEach((response) => {
      const list = response?.data?.bookingList || [];
      list.forEach((booking) => {
        if (booking?.id && !bookingMap.has(booking.id)) {
          bookingMap.set(booking.id, booking);
        }
      });
    });
    return Array.from(bookingMap.values());
  }

  private getAvailabilityTotalsFromRows(): {
    fullLengthAvailable: number;
    fullLengthTotal: number;
    halfLengthAvailable: number;
    halfLengthTotal: number;
  } {
    const totals = {
      fullLengthAvailable: 0,
      fullLengthTotal: 0,
      halfLengthAvailable: 0,
      halfLengthTotal: 0,
    };

    (this.slotStatusRows || []).forEach((row) => {
      const isFullLength = row.spotType.toLowerCase().includes('full length');
      const isOpen = row.bookingRows.length === 0;

      if (isFullLength) {
        totals.fullLengthTotal += 1;
        if (isOpen) {
          totals.fullLengthAvailable += 1;
        }
      } else {
        totals.halfLengthTotal += 1;
        if (isOpen) {
          totals.halfLengthAvailable += 1;
        }
      }
    });

    return totals;
  }

  private getAvailabilityTotalsForParkAnywhere(): {
    fullLengthAvailable: number;
    fullLengthTotal: number;
    halfLengthAvailable: number;
    halfLengthTotal: number;
  } {
    const totals = {
      fullLengthAvailable: 0,
      fullLengthTotal: 0,
      halfLengthAvailable: 0,
      halfLengthTotal: 0,
    };

    (this.parkingLotData?.truckType || []).forEach((truckType) => {
      const isFullLength = (truckType?.vehicleName || '')
        .toLowerCase()
        .includes('full-length');
      const count = Number(truckType?.numberOfSlots ?? 0);
      if (isFullLength) {
        totals.fullLengthTotal += count;
      } else {
        totals.halfLengthTotal += count;
      }
    });

    const bookingCounts = this.getActiveBookingCountsForParkAnywhere();
    totals.fullLengthAvailable = Math.max(
      totals.fullLengthTotal - bookingCounts.fullLength,
      0
    );
    totals.halfLengthAvailable = Math.max(
      totals.halfLengthTotal - bookingCounts.halfLength,
      0
    );

    return totals;
  }

  private getActiveBookingCountsForParkAnywhere(): {
    fullLength: number;
    halfLength: number;
  } {
    const counts = {
      fullLength: 0,
      halfLength: 0,
    };

    const rangeStart = this.startDate ? this.normalizeDate(this.startDate) : null;
    const rangeEnd = this.endDate ? this.normalizeDate(this.endDate) : null;

    (this.bookingList || []).forEach((booking) => {
      if (!this.isBookingActiveForSummary(booking)) {
        return;
      }

      const arrival = this.parseBookingDate(
        booking.startDate ?? booking.arrivalDate
      );
      const departure = this.parseBookingDate(
        booking.endDate ?? booking.departureDate
      );

      if (!this.isDateRangeOverlap(arrival, departure, rangeStart, rangeEnd)) {
        return;
      }

      const isFullLength = this.getBookingSpotTypeLabel(booking)
        .toLowerCase()
        .includes('full length');
      const spotCount = this.getBookingSpotCount(booking);

      if (isFullLength) {
        counts.fullLength += spotCount;
      } else {
        counts.halfLength += spotCount;
      }
    });

    return counts;
  }

  private isBookingActiveForSummary(booking: Booking): boolean {
    const status = (booking?.status || '').toLowerCase();
    return status !== 'cancelled' && status !== 'completed';
  }

  private isDateRangeOverlap(
    arrival: Date | null,
    departure: Date | null,
    rangeStart: Date | null,
    rangeEnd: Date | null
  ): boolean {
    if (!rangeStart || !rangeEnd) {
      return true;
    }
    const start = arrival ? arrival.getTime() : Number.MIN_SAFE_INTEGER;
    const end = departure ? departure.getTime() : Number.MAX_SAFE_INTEGER;
    return start <= rangeEnd.getTime() && end >= rangeStart.getTime();
  }

  private getBookingSpotTypeLabel(booking: Booking): string {
    return (
      booking?.truckType ||
      (booking as any)?.spotType ||
      (booking as any)?.vehicleType ||
      ''
    );
  }

  private getBookingSpotCount(booking: Booking): number {
    const count = Number((booking as any)?.spotCount ?? 1);
    return Number.isFinite(count) && count > 0 ? count : 1;
  }

  
}
